import {
	type DataProviderRequestDTO,
	DistributionBinningModeEnum,
	type ConceptDefinitionDTO,
	type ParameterizedConceptDTO,
} from "@eu.mdotm.api/smart-workspaces";
import z from "zod";
import { fakeTagConcept } from "./chain/ChainableElement";
import { chainSubmitSchema } from "./chain/schema";
import { workspaceChainModel, type WorkspaceChain } from "./chain/type";
import { chartTypeDrawsLegend } from "./chart-type";
import { autoGenerateMetricName } from "./common";
import type { WorkspaceChartCtx } from "./ctx/context";
import { isConceptCustomExposureTag } from "./repository";
import type { WorkspaceChartTypeEnum } from "./spec-gaps";

/**
 * The concept-shaped half of every panel's form model: all of {@link workspaceChainModel} — the fields the
 * exposure chain reads and writes — plus whether the name on screen is still the generated one.
 *
 * Derived from the model rather than declared beside it, like every form type in this area — see the note on
 * {@link workspaceChainModel} for the bug that buys. Every field is therefore listed exactly once, and
 * {@link formFromConcept} is keyed on `keyof` the result: a seeder that answers twelve of the thirteen is
 * what that guard exists to make uncompilable, and it still guards a genuine second enumeration, since the
 * seeder writes the field list out again as values.
 */
export const conceptFormModel = workspaceChainModel.extend({ autoGenerateName: z.boolean() });

export type ConceptFormFields = z.infer<typeof conceptFormModel>;

/**
 * `autoGenerateName` is *not* required at submit time, though the model says every form carries it.
 *
 * It is the only field on a panel that no payload reads — it decides whether the name box is disabled and
 * regenerated, nothing more — so a form that somehow reached Apply without it has nothing to correct, and a
 * blocked submit reporting a checkbox the user never touched would be the worst of both.
 */
const conceptFormSubmitShape = {
	autoGenerateName: z.boolean().optional(),
} satisfies Partial<Record<keyof ConceptFormFields, unknown>>;

/**
 * A concept-form model, plus the rules that only hold at submit time — {@link chainSubmitSchema} for the
 * chain underneath, and the one thing this half adds to it.
 *
 * Every panel's resolver is built through this, so "which fields does the resolver declare" has exactly one
 * answer: whatever its form model declares. A field can no longer reach the model without reaching the
 * schema, which is the whole reason the type is derived from it.
 */
export function conceptFormSubmitSchema<Shape extends z.ZodRawShape>(model: z.ZodObject<Shape>) {
	return chainSubmitSchema(model.extend(conceptFormSubmitShape));
}

/**
 * The key-metric threshold, as the chart panels ask for it.
 *
 * Its *mode* is not stored anywhere: `relativeThreshold` names a parameterized concept whose
 * `dataProvider` is the mode (Portfolio / Reference), `manualThreshold` is a plain number, and neither set
 * means Manual with no value. So the mode can only be recovered by resolving the concept — which is why
 * {@link FormFromConceptOptions.resolveConcept} exists, and why the seeder that forgot to resolve it
 * reopened a portfolio threshold as "Manual" and cleared it on the next Update.
 *
 * A model of its own because *which chart types own the setting* is a chart-type question: the chart panel spreads
 * these fields into the branches of its resolver union that accept them, so the union says which kinds have a
 * threshold instead of every kind silently accepting one.
 */
export const keyMetricThresholdModel = z.object({
	manualThreshold: z.number().nullable().optional(),
	dataProvider: z.custom<DataProviderRequestDTO>().nullable().optional(),
});

export type KeyMetricThresholdFormData = z.infer<typeof keyMetricThresholdModel>;

/**
 * The chart-shaped half: the fields a persisted *chart* owns rather than its concept.
 *
 * `chartName` and `description` are here rather than on {@link ConceptFormFields} because a chart's label
 * is its own — the concept's label seeds the chain's `name`, and the two are only equal while the name is
 * auto-generated.
 *
 * The breakdown *dimension* is deliberately absent: it is `breakdown` on the chain, because that is where
 * the user answered it (the "All" leaf) and where re-opening the panel has to show it still selected. Only
 * the display settings for the resulting categories are the chart's.
 */
export const chartFormModel = z
	.object({
		chartType: z.custom<WorkspaceChartTypeEnum>(),
		chartName: z.string().optional(),
		description: z.string().optional(),
		showLegend: z.boolean().optional(),
		breakdownLimit: z.number().nullable().optional(),
		breakdownOthersBucket: z.boolean().optional(),
		binningMode: z.nativeEnum(DistributionBinningModeEnum).optional(),
		binCount: z.number().nullable().optional(),
		binWidth: z.number().nullable().optional(),
		showMeanLines: z.boolean().optional(),
		showNormalFit: z.boolean().optional(),
	})
	.merge(keyMetricThresholdModel);

export type ChartFormFields = z.infer<typeof chartFormModel>;

/** What makes one caller of {@link formFromConcept} differ from another — and nothing else. */
export type FormFromConceptOptions = {
	/**
	 * The tag concepts to offer, when the concept is a custom exposure tag. Every caller fetches them from
	 * `getTagConceptDefinitions`; whether they are attached is this function's decision, not theirs.
	 */
	tagConcepts?: ConceptDefinitionDTO[];
	/**
	 * The chart the concept is charted by, when a chart is what is being edited. Its presence is what makes
	 * the four chart-only decisions at once, so they cannot be made half-way:
	 *
	 * - the {@link ChartFormFields} are seeded as well;
	 * - the auto-generated name is checked against the *chart's* label rather than the concept's;
	 * - it is generated with the chart's `breakdownProperties` folded in, since a stacked chart's stored
	 *   label says "by Sector" and the concept alone cannot reproduce it;
	 * - and with `ignoreSource`, because both chart panels render the chain with
	 *   `hideConceptSourceSelection` and a name mentioning a source the user cannot see would never match.
	 */
	chart?: WorkspaceChartCtx;
	/**
	 * Resolves a uuid in the concept pool. Only the chart's key-metric threshold needs it — see
	 * {@link KeyMetricThresholdFormData} for why the mode lives on a concept.
	 */
	resolveConcept?: (uuid: string) => ParameterizedConceptDTO | undefined;
	/**
	 * Forces `autoGenerateName` instead of deriving it. What the *create* panels pass: a panel that is
	 * seeding a brand-new entity from an existing concept has no persisted label to compare against, so the
	 * checkbox starts ticked and the name is generated from whatever the user does next.
	 */
	autoGenerateName?: boolean;
};

/**
 * A stored concept, as the form model that edits it — the one concept → form translation in the area.
 *
 * There were six of these, one per entry point into a panel (two chart, two filter, two column), and they
 * were the same twelve assignments written out six times. Two of them disagreed, which is how a portfolio
 * key-metric threshold reopened as "Manual" and was cleared by the next Update: the panel writes back the
 * mode it displays, so a seeder that fails to recover the mode does not merely mislabel it.
 *
 * The write direction is {@link workspaceChainToConceptDto} in `./concept`, and for a chart's series
 * `chartSeriesConcepts` in `./chart-type`. This is its mirror, and the two are the only translations
 * between a concept and a form in the area.
 *
 * Everything a *surface* owns and a concept does not stays with the caller: a filter's persisted condition
 * (`comparisonMode` / `comparisonValue`) is the filter's, not the concept's, and there are two of those
 * rather than six.
 */
export function formFromConcept(
	parametrizedConcept: ParameterizedConceptDTO,
	conceptDefinition: ConceptDefinitionDTO | undefined,
	opts?: FormFromConceptOptions,
): ConceptFormFields & Partial<ChartFormFields> {
	const chart = opts?.chart;
	// The chart's, not the concept's: a breakdown is never persisted on a parameterized concept (see
	// `PersistedConceptFields`), so the chart is the only place it can come back from.
	const breakdown = chart?.breakdownProperties ? { dimension: chart.breakdownProperties.dimension } : undefined;
	const isCustomTag = isConceptCustomExposureTag(parametrizedConcept.conceptId);

	const conceptFields = {
		autoGenerateName:
			opts?.autoGenerateName ??
			autoGenerateMetricName(
				{ parametrizedConcept: { ...parametrizedConcept, breakdown }, concept: conceptDefinition },
				{ ignoreSource: chart != null },
			) === (chart?.label ?? parametrizedConcept.label),
		concept: conceptDefinition,
		conceptUuid: parametrizedConcept.uuid,
		name: parametrizedConcept.label,
		timeHorizon: parametrizedConcept.timeHorizon,
		confidenceLevel: parametrizedConcept.confidenceLevel,
		representationMode: parametrizedConcept.representationMode,
		parameterizedRelativeness: parametrizedConcept.relativeness,
		source: parametrizedConcept.source,
		availableTagsConcepts: isCustomTag ? opts?.tagConcepts : undefined,
		fakeConceptId: isCustomTag ? fakeTagConcept.id : undefined,
		parameters: parametrizedConcept.parameters as WorkspaceChain["parameters"],
		breakdown,
	} satisfies Record<keyof ConceptFormFields, unknown>;

	if (!chart) {
		return conceptFields;
	}

	return {
		...conceptFields,
		...({
			chartType: chart.chartType,
			chartName: chart.label,
			description: chart.description,
			// Absent only on a chart persisted before the field existed. The fallback mirrors what the chart is
			// actually drawing in that case — the two legend-rendering types default it on, the other three
			// hardcode `legend: {enabled: false}` — so the checkbox never reopens contradicting the canvas.
			showLegend: chart.chartLegend?.visible ?? chartTypeDrawsLegend(chart.chartType),
			breakdownLimit: chart.breakdownProperties?.limit,
			breakdownOthersBucket: chart.breakdownProperties?.othersBucket,
			binningMode: chart.distributionProperties?.mode ?? DistributionBinningModeEnum.Auto,
			binCount: chart.distributionProperties?.binCount,
			binWidth: chart.distributionProperties?.binWidth,
			// Absent means on, so these two default the *opposite* way from `highlightProposal` in the same
			// bag — see the note on `chartPreferenceContent`. `densityOverlays` applies the same rule where the
			// chart itself reads them.
			showMeanLines: chart.preferences?.showMeanLines ?? true,
			showNormalFit: chart.preferences?.showNormalFit ?? true,
			manualThreshold: chart.manualThreshold,
			dataProvider: chart.relativeThreshold ? opts?.resolveConcept?.(chart.relativeThreshold)?.dataProvider : undefined,
		} satisfies Record<keyof ChartFormFields, unknown>),
	};
}
